import {
  Bars3BottomLeftIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/solid';
import { capitalize } from 'lodash';
import React from 'react';
import { toast } from 'sonner';

import { ThemeOption, useTheme } from '@/app/ThemeContext';
import { SystemThemeIcon } from '@/components/icons/SystemTheme.icon';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  useGetProfilePictureUrl,
  USER_DISPLAY_NAME_KEY,
  USER_PROFILE_PICTURE_ID,
} from '@/hooks/profilePicture';
import { useUploadFile } from '@/hooks/useFiles';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import {
  SettingDisplayCard,
  SettingEditableCard,
  UserProfileEditForm,
} from '@/sections/settings/components';
import { cn } from '@/utils/classname';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

interface ProfileSettingsSubsectionProps {
  me: string;
}

/**
 * ProfileSettingsSubsection handles user identity and appearance settings.
 *
 * Contains:
 * - User Profile card: Profile picture upload and display name editing
 * - Interface Theme card: Theme selection (system/dark/light)
 */
export const ProfileSettingsSubsection: React.FC<
  ProfileSettingsSubsectionProps
> = ({ me }) => {
  // Theme management
  const { theme: selectedTheme } = useTheme();

  // User settings with standard caching - always fetches for logged-in user
  const { data: settings, refetch: refetchSettings } = useMy(
    {
      resource: 'setting',
    },
    {
      email: me, // Always use own email, never impersonated
      staleTime: 30000, // 30 seconds - settings change infrequently
      gcTime: 300000, // 5 minutes - keep during navigation
    }
  );

  const displayNameSetting = settings?.find(
    s => s.name === USER_DISPLAY_NAME_KEY
  );
  const displayName =
    typeof displayNameSetting?.value === 'string'
      ? displayNameSetting.value
      : '';

  // Profile picture management - always for logged-in user
  const { data: profilePictureUrl, isFetching: isProfilePictureFetching } =
    useGetProfilePictureUrl({
      email: me, // Always use own email
      isOrganization: false,
    });

  const { mutateAsync: updateSetting } = useUpdateSetting(undefined); // No email override - always own profile

  const { mutate: uploadFile, status: uploadFileStatus } = useUploadFile(me);

  const isProfilePictureLoading =
    uploadFileStatus === 'pending' || isProfilePictureFetching;

  // Vulnerability grouping preference (persisted via localStorage)
  const [isGrouped, setIsGrouped] = useStorage<boolean>(
    { key: StorageKey.VULNERABILITY_GROUPING },
    false
  );

  /**
   * Handles saving display name with settings refetch for immediate UI update
   * Called as fire-and-forget from UserProfileEditForm (optimistic close pattern)
   */
  const handleSaveDisplayName = async (newValue: string) => {
    try {
      await updateSetting({
        name: USER_DISPLAY_NAME_KEY,
        value: newValue,
        silent: true,
      });
      await refetchSettings();
      toast.success('Name updated successfully');
    } catch (error) {
      console.error('Failed to save display name:', error);
      toast.error('Failed to update name. Please try again.');
    }
  };

  return (
    <>
      {/* Profile Settings Subsection */}
      <h3
        id="profile-settings-heading"
        className="text-chariot-text-primary mt-6 text-base font-bold first:mt-0"
        data-testid="profile-settings-subsection-header"
      >
        Profile Settings
      </h3>

      {/* Card 1: User Profile */}
      <SettingEditableCard
        data-testid="user-profile-card"
        editConfig={{
          modalTitle: 'Edit User Profile',
          formComponent: UserProfileEditForm,
          formProps: {
            currentName: displayName,
            currentPictureUrl: profilePictureUrl?.url,
            onUploadPicture: (buffer: ArrayBuffer) => {
              uploadFile({
                name: USER_PROFILE_PICTURE_ID,
                content: buffer,
              });
            },
            onDeletePicture: () => {
              uploadFile({
                name: USER_PROFILE_PICTURE_ID,
                content: '',
              });
            },
            onSaveName: handleSaveDisplayName,
            isUploadingPicture: uploadFileStatus === 'pending',
          },
          modalSize: 'lg',
        }}
        editable={true} // Always editable - this is own profile
      >
        {({ openModal: _openModal }) => (
          <div className="flex items-center gap-4">
            {/* Clickable profile picture */}
            {isProfilePictureLoading ? (
              <div className="border-chariot-stroke flex size-12 flex-col items-center justify-center gap-3 rounded-lg border-[3px] border-dashed">
                <Spinner size="lg" />
              </div>
            ) : (
              <ProfilePictureUpload
                source={
                  profilePictureUrl?.url
                    ? { type: 'url', url: profilePictureUrl.url }
                    : { type: 'empty' }
                }
                onUpload={buffer => {
                  uploadFile({
                    name: USER_PROFILE_PICTURE_ID,
                    content: buffer,
                  });
                }}
                onDelete={() => {
                  uploadFile({
                    name: USER_PROFILE_PICTURE_ID,
                    content: '',
                  });
                }}
                size="xs"
                shape="square"
                disabled={false} // Always enabled - own profile
                ariaLabel="Change profile picture"
                modalTitle="Change Profile Picture"
                emptyStateText="Click or drag and drop your profile picture here."
                data-testid="user-profile-picture-button"
              />
            )}
            <div className="flex flex-col gap-1">
              <h4 className="font-bold">User Profile</h4>
              <span className="text-chariot-text-secondary text-xs">
                {displayName || me}
              </span>
            </div>
          </div>
        )}
      </SettingEditableCard>

      {/* Card 2: Interface Theme */}
      <SettingDisplayCard data-testid="theme-card">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Interface Theme</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-secondary text-xs">
              Current:{' '}
              {selectedTheme === 'dark'
                ? 'Dark'
                : selectedTheme === 'light'
                  ? 'Light'
                  : 'System'}
            </span>
          </div>
        </div>

        {/* Theme buttons positioned on right */}
        <div className="absolute top-1/2 right-4 -translate-y-1/2">
          <div className="flex gap-2">
            <ThemeButton theme="system" disabled={false} />
            <ThemeButton theme="dark" disabled={false} />
            <ThemeButton theme="light" disabled={false} />
          </div>
        </div>
      </SettingDisplayCard>

      {/* Card 3: Vulnerability Grouping */}
      <SettingDisplayCard data-testid="vulnerability-grouping-card">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Vulnerability Grouping</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-secondary text-xs">
              Current: {isGrouped ? 'Grouped' : 'Ungrouped'}
            </span>
          </div>
        </div>

        {/* Grouping buttons positioned on right */}
        <div className="absolute top-1/2 right-4 -translate-y-1/2">
          <div className="flex gap-2">
            <GroupingButton
              mode="ungrouped"
              isSelected={!isGrouped}
              onClick={() => setIsGrouped(false)}
            />
            <GroupingButton
              mode="grouped"
              isSelected={isGrouped}
              onClick={() => setIsGrouped(true)}
            />
          </div>
        </div>
      </SettingDisplayCard>
    </>
  );
};

/**
 * ThemeButton - Theme selection button component
 */
function ThemeButton({
  theme,
  disabled = false,
}: {
  theme: ThemeOption;
  disabled?: boolean;
}) {
  const { theme: selectedTheme, setTheme } = useTheme();

  const isSelected = selectedTheme === theme;

  const themeIcon = {
    system: <SystemThemeIcon className="size-4" />,
    dark: <MoonIcon className="size-4" />,
    light: <SunIcon className="size-4" />,
  };

  return (
    <Button
      size="sm"
      className={cn(
        theme === 'light' &&
          !isSelected &&
          'border-chariot-stroke bg-chariot-stroke'
      )}
      variant={isSelected ? undefined : 'outline'}
      onClick={() => setTheme(theme)}
      disabled={disabled}
      data-testid={`theme-button-${theme}`}
    >
      {themeIcon[theme]}
      {capitalize(theme)}
    </Button>
  );
}

/**
 * GroupingButton - Vulnerability grouping selection button
 */
function GroupingButton({
  mode,
  isSelected,
  onClick,
}: {
  mode: 'grouped' | 'ungrouped';
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="sm"
      variant={isSelected ? undefined : 'outline'}
      onClick={onClick}
      data-testid={`grouping-button-${mode}`}
    >
      {mode === 'grouped' && <Bars3BottomLeftIcon className="size-4" />}
      {mode === 'grouped' ? 'Grouped' : 'Ungrouped'}
    </Button>
  );
}
